import { PadBox } from 'component/basics';
import { ActionButton, TextButton } from 'component/button';
import { colorButtonAccent } from 'component/color';
import type { Comment, CommentConfig } from 'component/comment';
import { UtilityText } from 'component/text';
import { ClipEditor } from 'open-modules/zdf/component/clipeditor';
import { ClipPlayer } from 'open-modules/zdf/component/clipplayer';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { getPrettyTimestamp, setVideoTime } from 'system/videotime';
import type { Datastore } from 'util/datastore';
import { useSessionData } from 'util/datastore';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export const VideoQuoteFeature: Feature<CommentConfig, {}> = {
    name: 'Video Quote',
    key: 'videoquote',
    providesConfig: {
        commentEditTopWidgets: [ToggleClipEditor],
        commentTopWidgets: [EmbeddedClip],
        commentPostCheckers: [checkClipAsync],
    },
};
type ToggleClipEditorProps = {
    comment: ClipComment;
    setComment: (c: ClipComment) => void;
};

export function ToggleClipEditor({ comment, setComment }: ToggleClipEditorProps): JSX.Element {
    const [clipAdded, setClipAdded] = useState(false);
    const editing = useSessionData(['editComment', comment.key]);

    function addClip(): void {
        setClipAdded(true);
    }

    function removeClip(): void {
        setComment({ ...comment, clip: undefined });
        setClipAdded(false);
    }

    return clipAdded || (comment.clip && editing) ? (
        <View>
            <RemoveClipButton onPress={removeClip} />
            <PadBox bottom={20}>
                <ClipEditor currentClip={comment.clip} onChangeValue={(clip) => setComment({ ...comment, clip })} />
            </PadBox>
        </View>
    ) : (
        <AddClipButton onPress={addClip} />
    );
}
type AddClipButtonProps = { onPress: () => void };

export function AddClipButton({ onPress }: AddClipButtonProps): JSX.Element {
    return (
        <PadBox bottom={20}>
            <ActionButton label={t('➕ Add Clip')} onPress={onPress} />
        </PadBox>
    );
}
type RemoveClipButtonProps = { onPress: () => void };

export function RemoveClipButton({ onPress }: RemoveClipButtonProps): JSX.Element {
    return (
        <PadBox bottom={20}>
            <ActionButton label={t('➖ Remove Clip')} onPress={onPress} />
        </PadBox>
    );
}
type EmbeddedClipProps = { comment: ClipComment };

export function EmbeddedClip({ comment }: EmbeddedClipProps): JSX.Element | undefined {
    const editing = useSessionData(['editComment', comment.key]);

    // Don't show clip player if there is no clip attached to the comment
    if (!comment.clip) {
        return;
    }

    // Don't show clip player if the comment is being edited and the clip editor is open
    if (editing) {
        return;
    }

    function jumpToStartTime() {
        setVideoTime(comment.clip!.start);
    }

    function jumpToEndTime() {
        setVideoTime(comment.clip!.end);
    }

    return (
        <View style={VideoQuoteStyle.embeddedClip}>
            <ClipPlayer clip={comment.clip} />
            {/* For the timestamps, we need to check for null specifically because 0 is a valid timestamp */}
            {comment.clip &&
            comment.clip.source &&
            comment.clip.start !== null &&
            comment.clip.start !== undefined &&
            comment.clip.end !== null &&
            comment.clip.end !== undefined ? (
                <View style={VideoQuoteStyle.clipSource}>
                    <UtilityText color='gray' label={t('↪ Referencing ')} />
                    <TextButton
                        color={colorButtonAccent}
                        onPress={jumpToStartTime}
                        text={getPrettyTimestamp(comment.clip.start)}
                        underline={true}
                    />
                    <UtilityText color='gray' label={t(' - ')} />
                    <TextButton
                        color={colorButtonAccent}
                        onPress={jumpToEndTime}
                        text={getPrettyTimestamp(comment.clip.end)}
                        underline={true}
                    />
                </View>
            ) : null}
        </View>
    );
}

export type ClipComment = Comment & { clip?: { start: number; end: number; valid?: boolean; source?: string } };

export async function checkClipAsync(datastore: Datastore, comment: ClipComment, questionText: string): Promise<{ allow: boolean }> {
    // Don't post comments with invalid clips
    return {
        allow: comment.clip ? !!comment.clip.valid : true,
    };
}

const VideoQuoteStyle = StyleSheet.create({
    embeddedClip: {
        paddingBottom: 20,
    },
    clipSource: {
        paddingTop: 10,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
    },
});
